import type React from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Card, Heading, ShowHide } from '@zillow/constellation-10';

import MarketPreview from './MarketPreview';
import FilterTray from 'app/shared/components/FilterTray';
import { useMarketTrendsFilters } from 'app/shared/hooks/useFilters';
import { useAppDispatch } from 'app/shared/flux/hooks';

import type { State } from 'app/shared/types/State';
import type { Market } from 'app/shared/types/Market';

interface MarketPreviewsProps {
  markets: Array<Market>;
  heading: string;
  subject: string;
  useFilters?: boolean;
  isSmallScreen: boolean;
  isMobile: boolean;
  emitAnalyticsEvent?: () => void;
}

const MarketPreviews: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<MarketPreviewsProps>>> = (
  props,
) => {
  const { heading, subject, useFilters = false, isSmallScreen, isMobile, emitAnalyticsEvent } = props;
  const dispatch = useAppDispatch();
  const filter = useSelector((state: State) => state.filter);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { bedrooms, setBedrooms, propertyTypes, setPropertyTypes } = useMarketTrendsFilters(filter, dispatch);

  const markets = props.markets.filter((market) => market.medianRent !== 0); // dont include when theres no data
  const filterDisplay = (
    <FilterTray
      bedrooms={bedrooms}
      setBedrooms={setBedrooms}
      propertyTypes={propertyTypes}
      setPropertyTypes={setPropertyTypes}
      loading={loading}
      setLoading={setLoading}
      isMobile={isMobile}
      style={isSmallScreen ? {} : { marginRight: 60 }}
    />
  );
  const isCollapsible = markets.length > 4 && isSmallScreen;
  const renderMarket = (market: Market) => (
    <MarketPreview
      {...market}
      isSmallScreen={isSmallScreen}
      key={market.name}
      emitAnalyticsEvent={emitAnalyticsEvent}
    />
  );
  const marketGrid = (
    <Box
      css={{
        display: 'grid',
        gap: isSmallScreen ? '200' : '800',
        marginBottom: '800',
        // the filters act as a spacer, so need to add margin when the filters are not present
        marginTop: useFilters ? undefined : '600',
        gridAutoFlow: 'row',
        paddingRight: isSmallScreen ? '' : '1500',
        gridTemplateColumns: isSmallScreen ? `repeat(auto-fill, 160px)` : `repeat(auto-fill, minmax(170px, 1fr))`,
      }}
    >
      {isCollapsible
        ? [
            ...markets.slice(0, 4).map(renderMarket),
            <ShowHide.Hidden key="hidden-markets">{markets.slice(4).map(renderMarket)}</ShowHide.Hidden>,
          ]
        : markets.map(renderMarket)}
    </Box>
  );
  return (
    <Card
      tone="neutral"
      css={{
        padding: '600',
        paddingInline: isSmallScreen ? undefined : '600',
        marginTop: '600',
      }}
    >
      <Heading level={2} textStyle="heading-sm" fontFamily="heading-alt">
        {heading}
      </Heading>
      {useFilters &&
        (isSmallScreen ? (
          filterDisplay
        ) : (
          <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {filterDisplay}
          </Box>
        ))}

      {isCollapsible ? (
        <ShowHide.Root
          isHidden={!isExpanded}
          onDisplayChange={() => setIsExpanded((prev) => !prev)}
          renderButton={(buttonProps) => (
            <ShowHide.Button aria-controls={buttonProps['aria-controls']}>
              {isExpanded ? `See less ${subject}` : `See more ${subject}`}
            </ShowHide.Button>
          )}
        >
          {marketGrid}
        </ShowHide.Root>
      ) : (
        marketGrid
      )}
    </Card>
  );
};

export default MarketPreviews;
